/*
  Formulas of the "table" question.

  The same evaluator exists in kevi-api (Helper\Forms\TableFormula) and a copy
  of this file lives in kevi-web-admin. All three must give the same results:
  tableFormulaCases.json is run against each of them.

  Grammar: numbers (dot decimal), "text", cell references (B2), ranges (A1:D1,
  B1:B5, B:B), column aliases, + - * / ( ), unary minus, comparisons
  (= <> < > <= >=) and the functions below. No eval: formulas are written by
  any place admin and run on a public page.

  References use the editor's numbering: data rows first (the template row of a
  dynamic table counts as row 1), then the summary rows. In a dynamic table a
  data-row formula is relative to the row being evaluated, and B:B always means
  every filled data row, never the summary rows.
*/

export class FormulaError extends Error {
    constructor(code, message) {
        super(message || code);
        this.code = code;
    }
}

const FUNCTIONS = {
    // Calculator action functions, same behaviour as there.
    MAX: { min: 2, max: Infinity, aggregate: true },
    MIN: { min: 2, max: Infinity, aggregate: true },
    ROUND: { min: 1, max: 2 },
    FLOOR: { min: 1, max: 1 },
    CEIL: { min: 1, max: 1 },
    ABS: { min: 1, max: 1 },
    // Spreadsheet functions.
    SUM: { min: 1, max: Infinity, aggregate: true },
    AVERAGE: { min: 1, max: Infinity, aggregate: true },
    COUNT: { min: 1, max: Infinity, aggregate: true },
    IF: { min: 3, max: 3, lazy: true },
};

export const FUNCTION_NAMES = Object.keys(FUNCTIONS);

const CELL_RE = /^([A-Z]{1,2})([0-9]{1,4})$/;
const COLUMN_RE = /^[A-Z]{1,2}$/;

export function columnLetter(index) {
    let n = index + 1;
    let s = "";
    while (n > 0) {
        const r = (n - 1) % 26;
        s = String.fromCharCode(65 + r) + s;
        n = Math.floor((n - 1) / 26);
    }
    return s;
}

export function columnIndex(letters) {
    let n = 0;
    for (let i = 0; i < letters.length; i++) {
        n = n * 26 + (letters.charCodeAt(i) - 64);
    }
    return n - 1;
}

// ---------------------------------------------------------------- tokenizer

function tokenize(src) {
    const tokens = [];
    let i = 0;
    while (i < src.length) {
        const c = src[i];
        if (c === " " || c === "\t" || c === "\n" || c === "\r") {
            i++;
            continue;
        }
        if (/[0-9]/.test(c) || (c === "." && /[0-9]/.test(src[i + 1] || ""))) {
            let j = i;
            while (j < src.length && /[0-9]/.test(src[j])) j++;
            if (src[j] === ".") {
                j++;
                while (j < src.length && /[0-9]/.test(src[j])) j++;
            }
            if (/[A-Za-z_.]/.test(src[j] || "")) {
                throw new FormulaError("syntax", "Número inválido");
            }
            tokens.push({ t: "num", v: parseFloat(src.slice(i, j)) });
            i = j;
            continue;
        }
        if (/[A-Za-z_]/.test(c)) {
            let j = i;
            while (j < src.length && /[A-Za-z0-9_]/.test(src[j])) j++;
            tokens.push({ t: "id", v: src.slice(i, j) });
            i = j;
            continue;
        }
        if (c === "\"") {
            let j = i + 1;
            let s = "";
            while (j < src.length && src[j] !== "\"") {
                s += src[j];
                j++;
            }
            if (j >= src.length) {
                throw new FormulaError("syntax", "Texto sem aspas de fechamento");
            }
            tokens.push({ t: "str", v: s });
            i = j + 1;
            continue;
        }
        const two = src.slice(i, i + 2);
        if (two === "<>" || two === "<=" || two === ">=") {
            tokens.push({ t: "op", v: two });
            i += 2;
            continue;
        }
        if ("+-*/(),:=<>".indexOf(c) !== -1) {
            tokens.push({ t: "op", v: c });
            i++;
            continue;
        }
        throw new FormulaError("syntax", "Caractere inválido: " + c);
    }
    return tokens;
}

// ------------------------------------------------------------------- parser

/*
  AST nodes:
    {k: "num", v}            {k: "str", v}
    {k: "cell", col, row}    {k: "range", c1, r1, c2, r2}  (r1/r2 null = whole column)
    {k: "alias", name}
    {k: "neg", e}            {k: "bin", op, l, r}
    {k: "call", name, args}
*/
// Formulas come from any place admin; these limits keep a pathological one
// from exhausting the stack or the CPU (same limits in kevi-api).
export const MAX_FORMULA_LENGTH = 1000;
export const MAX_FORMULA_DEPTH = 64;

export function parseFormula(src) {
    const text = String(src == null ? "" : src).replace(/^\s*=/, "");
    if (!text.trim()) {
        throw new FormulaError("syntax", "Fórmula vazia");
    }
    if (text.length > MAX_FORMULA_LENGTH) {
        throw new FormulaError("too_complex", "Fórmula longa demais");
    }
    const tokens = tokenize(text);
    let pos = 0;
    let depth = 0;
    const enter = () => {
        depth += 1;
        if (depth > MAX_FORMULA_DEPTH) {
            throw new FormulaError("too_complex", "Fórmula aninhada demais");
        }
    };

    const peek = () => tokens[pos];
    const isOp = (v) => peek() && peek().t === "op" && peek().v === v;
    const expectOp = (v) => {
        if (!isOp(v)) {
            throw new FormulaError("syntax", "Esperado " + v);
        }
        pos++;
    };

    function comparison() {
        let left = additive();
        const t = peek();
        if (t && t.t === "op" && ["=", "<>", "<", ">", "<=", ">="].indexOf(t.v) !== -1) {
            pos++;
            left = { k: "bin", op: t.v, l: left, r: additive() };
        }
        return left;
    }
    function additive() {
        let left = term();
        while (isOp("+") || isOp("-")) {
            const op = tokens[pos++].v;
            left = { k: "bin", op, l: left, r: term() };
        }
        return left;
    }
    function term() {
        let left = unary();
        while (isOp("*") || isOp("/")) {
            const op = tokens[pos++].v;
            left = { k: "bin", op, l: left, r: unary() };
        }
        return left;
    }
    function unary() {
        if (isOp("-")) {
            pos++;
            enter();
            const e = unary();
            depth -= 1;
            return { k: "neg", e };
        }
        if (isOp("+")) {
            pos++;
            return unary();
        }
        return primary();
    }
    function primary() {
        const t = peek();
        if (!t) {
            throw new FormulaError("syntax", "Fórmula incompleta");
        }
        if (t.t === "num") {
            pos++;
            return { k: "num", v: t.v };
        }
        if (t.t === "str") {
            pos++;
            return { k: "str", v: t.v };
        }
        if (isOp("(")) {
            pos++;
            enter();
            const e = comparison();
            expectOp(")");
            depth -= 1;
            return e;
        }
        if (t.t === "id") {
            pos++;
            const upper = t.v.toUpperCase();
            if (isOp("(")) {
                if (!FUNCTIONS[upper]) {
                    throw new FormulaError("function", "Função desconhecida: " + t.v);
                }
                pos++;
                enter();
                const args = [];
                if (!isOp(")")) {
                    args.push(argument());
                    while (isOp(",")) {
                        pos++;
                        args.push(argument());
                    }
                }
                expectOp(")");
                depth -= 1;
                checkArity(upper, args);
                return { k: "call", name: upper, args };
            }
            const cell = CELL_RE.exec(upper);
            if (cell) {
                return { k: "cell", col: columnIndex(cell[1]), row: parseInt(cell[2], 10) };
            }
            if (COLUMN_RE.test(upper) && isOp(":")) {
                // B:B, B:D (whole columns)
                pos++;
                const next = peek();
                if (!next || next.t !== "id" || !COLUMN_RE.test(next.v.toUpperCase())) {
                    throw new FormulaError("syntax", "Intervalo inválido");
                }
                pos++;
                return {
                    k: "range",
                    c1: columnIndex(upper),
                    r1: null,
                    c2: columnIndex(next.v.toUpperCase()),
                    r2: null,
                };
            }
            if (FUNCTIONS[upper]) {
                throw new FormulaError("syntax", "Esperado ( depois de " + t.v);
            }
            return { k: "alias", name: t.v.toLowerCase() };
        }
        throw new FormulaError("syntax", "Símbolo inesperado: " + t.v);
    }
    // Ranges are only valid as function arguments.
    function argument() {
        const start = pos;
        const t = peek();
        if (t && t.t === "id" && CELL_RE.test(t.v.toUpperCase()) && tokens[pos + 1] &&
            tokens[pos + 1].t === "op" && tokens[pos + 1].v === ":") {
            const a = CELL_RE.exec(t.v.toUpperCase());
            pos += 2;
            const n = peek();
            const b = n && n.t === "id" ? CELL_RE.exec(n.v.toUpperCase()) : null;
            if (!b) {
                pos = start;
                throw new FormulaError("syntax", "Intervalo inválido");
            }
            pos++;
            return {
                k: "range",
                c1: columnIndex(a[1]),
                r1: parseInt(a[2], 10),
                c2: columnIndex(b[1]),
                r2: parseInt(b[2], 10),
            };
        }
        return comparison();
    }

    const ast = comparison();
    if (pos < tokens.length) {
        const t = tokens[pos];
        if (t.t === "op" && t.v === ",") {
            // Same hint the calculator gives for a decimal comma.
            throw new FormulaError("decimal_comma", "Use ponto como separador decimal");
        }
        if (t.t === "op" && t.v === ":") {
            throw new FormulaError("range_outside", "Intervalos só podem ser usados dentro de funções");
        }
        throw new FormulaError("syntax", "Símbolo inesperado: " + t.v);
    }
    assertNoBareRanges(ast, false);
    return ast;
}

function checkArity(name, args) {
    const def = FUNCTIONS[name];
    const hasRange = args.some((a) => a.k === "range" || a.k === "alias");
    const min = def.aggregate && hasRange ? 1 : def.min;
    if (args.length < min || args.length > def.max) {
        throw new FormulaError("arity", "Número de argumentos inválido em " + name);
    }
}

function assertNoBareRanges(node, insideCall) {
    if (!node) return;
    if (node.k === "range" && !insideCall) {
        throw new FormulaError("range_outside", "Intervalos só podem ser usados dentro de funções");
    }
    if (node.k === "call") {
        node.args.forEach((a) => {
            if (a.k !== "range") assertNoBareRanges(a, false);
        });
        return;
    }
    if (node.k === "bin") {
        assertNoBareRanges(node.l, insideCall);
        assertNoBareRanges(node.r, insideCall);
    }
    if (node.k === "neg") assertNoBareRanges(node.e, insideCall);
}

// ------------------------------------------------------------- serializing

export function formulaToString(node) {
    switch (node.k) {
        case "num":
            return String(node.v);
        case "str":
            return "\"" + node.v + "\"";
        case "cell":
            return columnLetter(node.col) + node.row;
        case "range":
            return node.r1 == null
                ? columnLetter(node.c1) + ":" + columnLetter(node.c2)
                : columnLetter(node.c1) + node.r1 + ":" + columnLetter(node.c2) + node.r2;
        case "alias":
            return node.name;
        case "neg":
            return "-" + wrap(node.e, 3);
        case "call":
            return node.name + "(" + node.args.map(formulaToString).join(", ") + ")";
        case "bin": {
            const p = precedence(node.op);
            return wrap(node.l, p) + " " + node.op + " " + wrap(node.r, p + (node.op === "-" || node.op === "/" ? 1 : 0));
        }
    }
    return "";
}

function precedence(op) {
    if (op === "*" || op === "/") return 2;
    if (op === "+" || op === "-") return 1;
    return 0;
}

function wrap(node, minPrecedence) {
    const s = formulaToString(node);
    if (node.k === "bin" && precedence(node.op) < minPrecedence) {
        return "(" + s + ")";
    }
    return s;
}

// ---------------------------------------------------------------- values

function isEmpty(v) {
    return v === null || v === undefined || v === "";
}

function toNumber(v) {
    if (isEmpty(v)) return 0;
    if (v === true) return 1;
    if (v === false) return 0;
    if (typeof v === "number") return v;
    throw new FormulaError("value", "Texto usado numa conta");
}

function truthy(v) {
    if (typeof v === "string") return v !== "";
    return toNumber(v) !== 0;
}

// PHP round(): half away from zero, with the float fix PHP applies.
export function phpRound(value, precision) {
    const p = precision === undefined ? 0 : Math.trunc(precision);
    const factor = Math.pow(10, p);
    const scaled = value * factor;
    const pre = parseFloat(scaled.toPrecision(15));
    const rounded = pre >= 0 ? Math.floor(pre + 0.5) : -Math.floor(-pre + 0.5);
    return rounded / factor;
}

function compare(op, a, b) {
    let x = a;
    let y = b;
    if (typeof a === "string" || typeof b === "string") {
        x = isEmpty(a) ? "" : String(a).toLowerCase();
        y = isEmpty(b) ? "" : String(b).toLowerCase();
    } else {
        x = toNumber(a);
        y = toNumber(b);
    }
    switch (op) {
        case "=":
            return x === y;
        case "<>":
            return x !== y;
        case "<":
            return x < y;
        case ">":
            return x > y;
        case "<=":
            return x <= y;
        case ">=":
            return x >= y;
    }
    return false;
}

function numbersOf(list) {
    return list.filter((v) => typeof v === "number" || typeof v === "boolean").map(toNumber);
}

function callFunction(name, args) {
    switch (name) {
        case "SUM":
            return numbersOf(args.flat()).reduce((a, b) => a + b, 0);
        case "AVERAGE": {
            const n = numbersOf(args.flat());
            if (!n.length) throw new FormulaError("div0", "Média sem valores");
            return n.reduce((a, b) => a + b, 0) / n.length;
        }
        case "COUNT":
            return numbersOf(args.flat()).length;
        case "MAX":
        case "MIN": {
            const n = numbersOf(args.flat());
            if (!n.length) return 0;
            return name === "MAX" ? Math.max(...n) : Math.min(...n);
        }
        case "ROUND": {
            // Same clamp as kevi-api: beyond 15 digits there is nothing to round.
            const places = args.length > 1 ? toNumber(args[1]) : 0;
            return phpRound(toNumber(args[0]), Math.max(-15, Math.min(15, places)));
        }
        case "FLOOR":
            return Math.floor(toNumber(args[0]));
        case "CEIL":
            return Math.ceil(toNumber(args[0]));
        case "ABS":
            return Math.abs(toNumber(args[0]));
    }
    throw new FormulaError("function", "Função desconhecida: " + name);
}

/*
  ctx:
    cell(col, row)        -> value of a single cell (editor numbering)
    range(c1, r1, c2, r2) -> array of values (r1 null = whole columns)
    alias(name, asRange)  -> column index or throws
*/
export function evaluate(node, ctx) {
    switch (node.k) {
        case "num":
            return node.v;
        case "str":
            return node.v;
        case "cell":
            return ctx.cell(node.col, node.row);
        case "alias":
            return ctx.cell(ctx.alias(node.name), null);
        case "range":
            return ctx.range(node.c1, node.r1, node.c2, node.r2);
        case "neg":
            return -toNumber(evaluate(node.e, ctx));
        case "bin": {
            const l = evaluate(node.l, ctx);
            const r = evaluate(node.r, ctx);
            switch (node.op) {
                case "+":
                    return toNumber(l) + toNumber(r);
                case "-":
                    return toNumber(l) - toNumber(r);
                case "*":
                    return toNumber(l) * toNumber(r);
                case "/": {
                    const d = toNumber(r);
                    if (d === 0) throw new FormulaError("div0", "Divisão por zero");
                    return toNumber(l) / d;
                }
                default:
                    return compare(node.op, l, r);
            }
        }
        case "call": {
            if (node.name === "IF") {
                return truthy(evaluate(node.args[0], ctx))
                    ? evaluate(node.args[1], ctx)
                    : evaluate(node.args[2], ctx);
            }
            const args = node.args.map((a) => {
                if (a.k === "alias" && FUNCTIONS[node.name].aggregate && ctx.aliasAsColumn) {
                    const col = ctx.alias(a.name);
                    return ctx.range(col, null, col, null);
                }
                const v = evaluate(a, ctx);
                return Array.isArray(v) ? v : [v];
            });
            if (FUNCTIONS[node.name].aggregate) {
                return callFunction(node.name, args);
            }
            return callFunction(node.name, args.map((a) => a[0]));
        }
    }
    throw new FormulaError("syntax", "Fórmula inválida");
}

// ------------------------------------------------------------ table model

/*
  settings (question.settings of a "table" question):
    columns: [{label, type, alias?, required?, minWidth?, min?, max?, decimals?, prefix?, suffix?, options?}]
    rows: {
      mode: "dynamic" | "fixed",
      min?, max?, addLabel?,               (dynamic)
      template?: {cells: [cfg|null, ...]}, (dynamic)
      items?: [{label, cells}],            (fixed)
      summary?: [{label, cells}]
    }
    cfg = {value?, locked?, formula?}
*/

export function dataRowConfigs(settings) {
    const rows = (settings && settings.rows) || {};
    if (rows.mode === "fixed") {
        return (rows.items || []).map((item) => (item && item.cells) || []);
    }
    return [((rows.template && rows.template.cells) || [])];
}

export function cellConfig(settings, rowIndex, colIndex) {
    const rows = (settings && settings.rows) || {};
    const cells = rows.mode === "fixed"
        ? ((rows.items || [])[rowIndex] || {}).cells
        : (rows.template || {}).cells;
    return (cells || [])[colIndex] || null;
}

export function summaryCellConfig(settings, summaryIndex, colIndex) {
    const summary = ((settings && settings.rows) || {}).summary || [];
    return ((summary[summaryIndex] || {}).cells || [])[colIndex] || null;
}

export function isLockedConfig(cfg) {
    return !!(cfg && (cfg.locked || (cfg.formula !== undefined && cfg.formula !== null && cfg.formula !== "")));
}

export function hasFormula(cfg) {
    return !!(cfg && typeof cfg.formula === "string" && cfg.formula.trim() !== "");
}

function aliasMap(columns) {
    const map = {};
    (columns || []).forEach((col, i) => {
        if (col && col.alias) map[String(col.alias).toLowerCase()] = i;
    });
    return map;
}

/*
  A data row is "filled" when an editable (not locked, no formula) cell has a
  value. Empty dynamic rows are dropped before evaluating, so they do not count
  in COUNT/AVERAGE and are not stored.
*/
export function isRowFilled(settings, row) {
    const columns = settings.columns || [];
    const template = dataRowConfigs(settings)[0] || [];
    for (let j = 0; j < columns.length; j++) {
        if (isLockedConfig(template[j])) continue;
        if (!isEmpty(row[j])) {
            if (columns[j].type === "number" && Number(row[j]) === 0 && typeof row[j] === "number") continue;
            return true;
        }
    }
    return false;
}

/*
  Evaluates every formula cell of the table.

  values: data rows as arrays aligned with columns (dynamic: the filled rows,
  fixed: one per item). Locked cells are reset to their configured value.

  Returns {values, summary, errors} where errors maps "r:c" (data) and
  "s:k:c" (summary) to an error code.
*/
export function computeTable(settings, inputValues) {
    const columns = settings.columns || [];
    const n = columns.length;
    const rows = settings.rows || {};
    const fixed = rows.mode === "fixed";
    const summaryDefs = rows.summary || [];
    const aliases = aliasMap(columns);
    const dataCount = (inputValues || []).length;
    const editorDataRows = fixed ? (rows.items || []).length : 1;

    const values = (inputValues || []).map((row, i) => {
        const out = [];
        for (let j = 0; j < n; j++) {
            const cfg = fixed ? cellConfig(settings, i, j) : cellConfig(settings, 0, j);
            let v = row && row[j] !== undefined ? row[j] : null;
            if (hasFormula(cfg)) v = null;
            else if (cfg && cfg.locked) v = cfg.value === undefined ? null : cfg.value;
            out.push(v);
        }
        return out;
    });
    const summary = summaryDefs.map(() => new Array(n).fill(null));
    const errors = {};

    // Parse every formula once.
    const parsed = {};
    const parseCell = (key, cfg) => {
        if (!hasFormula(cfg)) return;
        try {
            parsed[key] = parseFormula(cfg.formula);
        } catch (e) {
            parsed[key] = { error: e.code || "syntax" };
        }
    };
    for (let i = 0; i < editorDataRows; i++) {
        for (let j = 0; j < n; j++) parseCell("d:" + i + ":" + j, cellConfig(settings, i, j));
    }
    summaryDefs.forEach((s, k) => {
        for (let j = 0; j < n; j++) parseCell("s:" + k + ":" + j, summaryCellConfig(settings, k, j));
    });

    const order = formulaOrder(settings, parsed);
    if (order.cycle) {
        order.cycle.forEach((key) => {
            const [kind, a, b] = key.split(":");
            if (kind === "d") {
                if (fixed) errors[a + ":" + b] = "circular";
                else for (let i = 0; i < dataCount; i++) errors[i + ":" + b] = "circular";
            } else {
                errors["s:" + a + ":" + b] = "circular";
            }
        });
    }

    const rangeValues = (c1, r1, c2, r2, currentRow) => {
        const out = [];
        const cLo = Math.min(c1, c2);
        const cHi = Math.max(c1, c2);
        for (let c = cLo; c <= cHi; c++) {
            if (r1 == null) {
                for (let i = 0; i < dataCount; i++) out.push(values[i][c]);
            } else {
                const rLo = Math.min(r1, r2);
                const rHi = Math.max(r1, r2);
                for (let r = rLo; r <= rHi; r++) out.push(cellAt(c, r, currentRow));
            }
        }
        return out;
    };

    // Single cell by editor numbering. currentRow is the actual data row being
    // evaluated (dynamic tables), or null in summary rows.
    const cellAt = (col, row, currentRow) => {
        if (col < 0 || col >= n) throw new FormulaError("ref", "Referência inválida");
        if (row === null) {
            if (currentRow === null) throw new FormulaError("ref", "Use a coluna inteira numa linha de resumo");
            return values[currentRow][col];
        }
        if (row >= 1 && row <= editorDataRows) {
            if (fixed) return values[row - 1] ? values[row - 1][col] : null;
            if (currentRow === null) throw new FormulaError("ref", "Use a coluna inteira numa linha de resumo");
            return values[currentRow][col];
        }
        const k = row - editorDataRows - 1;
        if (k >= 0 && k < summary.length) return summary[k][col];
        throw new FormulaError("ref", "Referência inválida");
    };

    const contextFor = (currentRow) => ({
        cell: (col, row) => cellAt(col, row, currentRow),
        range: (c1, r1, c2, r2) => rangeValues(c1, r1, c2, r2, currentRow),
        alias: (name) => {
            if (aliases[name] === undefined) throw new FormulaError("ref", "Apelido desconhecido: " + name);
            return aliases[name];
        },
        aliasAsColumn: true,
    });
    const summaryContext = () => {
        const ctx = contextFor(null);
        // A bare alias in a summary row means the whole column.
        ctx.cell = (col, row) => {
            if (row === null) throw new FormulaError("ref", "Use a coluna inteira numa linha de resumo");
            return cellAt(col, row, null);
        };
        return ctx;
    };

    const run = (key, ast, ctx, setValue, errorKey) => {
        if (errors[errorKey] === "circular") return;
        if (ast.error) {
            errors[errorKey] = ast.error;
            setValue(null);
            return;
        }
        try {
            let v = evaluate(ast, ctx);
            if (typeof v === "boolean") v = v ? 1 : 0;
            if (typeof v === "number" && !isFinite(v)) throw new FormulaError("div0", "Divisão por zero");
            // Formulas only live in number columns: text (IF(…, A1, 0) with a
            // text A1) is not a result.
            if (typeof v !== "number") throw new FormulaError("value", "Resultado não é um número");
            setValue(cleanFloat(v));
        } catch (e) {
            errors[errorKey] = e.code || "value";
            setValue(null);
        }
    };

    order.keys.forEach((key) => {
        const ast = parsed[key];
        const [kind, a, b] = key.split(":");
        const col = parseInt(b, 10);
        if (kind === "d") {
            const editorRow = parseInt(a, 10);
            if (fixed) {
                if (!values[editorRow]) return;
                run(key, ast, contextFor(editorRow), (v) => { values[editorRow][col] = v; }, editorRow + ":" + col);
            } else {
                for (let i = 0; i < dataCount; i++) {
                    run(key, ast, contextFor(i), (v) => { values[i][col] = v; }, i + ":" + col);
                }
            }
        } else {
            const k = parseInt(a, 10);
            run(key, ast, summaryContext(), (v) => { summary[k][col] = v; }, "s:" + k + ":" + col);
        }
    });

    return { values, summary, errors };
}

// Float noise (0.1 + 0.2) must not leak into stored answers or text.
// Integers are left alone so long ones keep every digit (same in kevi-api).
function cleanFloat(v) {
    if (Number.isInteger(v)) return v;
    return parseFloat(v.toPrecision(12));
}

/*
  Topological order of formula cells, at editor level ("d:row:col" for data
  rows, "s:k:col" for summary rows). Returns {keys, cycle}.
*/
export function formulaOrder(settings, parsed) {
    const columns = settings.columns || [];
    const columnCount = columns.length;
    const rows = settings.rows || {};
    const fixed = rows.mode === "fixed";
    const editorDataRows = fixed ? (rows.items || []).length : 1;
    const totalRows = editorDataRows + (rows.summary || []).length;
    const aliases = aliasMap(columns);
    const keys = Object.keys(parsed);
    const isFormula = (key) => Object.prototype.hasOwnProperty.call(parsed, key);

    const editorKey = (col, row, fromKey) => {
        if (row === null) {
            const [kind, a] = fromKey.split(":");
            return kind === "d" ? "d:" + (fixed ? a : 0) + ":" + col : null;
        }
        if (row >= 1 && row <= editorDataRows) return "d:" + (row - 1) + ":" + col;
        const k = row - editorDataRows - 1;
        return "s:" + k + ":" + col;
    };

    const deps = {};
    keys.forEach((key) => {
        const out = new Set();
        const ast = parsed[key];
        if (!ast || ast.error) {
            deps[key] = out;
            return;
        }
        const visit = (node, inAggregate) => {
            if (!node) return;
            if (node.k === "cell") {
                const k = editorKey(node.col, node.row, key);
                if (k && isFormula(k)) out.add(k);
            } else if (node.k === "alias") {
                const col = aliases[node.name];
                if (col === undefined) return;
                if (inAggregate || key.startsWith("s:")) {
                    for (let r = 0; r < editorDataRows; r++) if (isFormula("d:" + r + ":" + col)) out.add("d:" + r + ":" + col);
                } else {
                    const k = editorKey(col, null, key);
                    if (k && isFormula(k)) out.add(k);
                }
            } else if (node.k === "range") {
                // Clamped to the table: a reference outside it is reported by
                // the validation, and must not cost a loop over thousands of
                // cells that do not exist.
                const cLo = Math.min(node.c1, node.c2);
                const cHi = Math.min(Math.max(node.c1, node.c2), columnCount - 1);
                for (let c = cLo; c <= cHi; c++) {
                    if (node.r1 == null) {
                        for (let r = 0; r < editorDataRows; r++) if (isFormula("d:" + r + ":" + c)) out.add("d:" + r + ":" + c);
                    } else {
                        const rLo = Math.min(node.r1, node.r2);
                        const rHi = Math.min(Math.max(node.r1, node.r2), totalRows);
                        for (let r = rLo; r <= rHi; r++) {
                            const k = editorKey(c, r, key);
                            if (k && isFormula(k)) out.add(k);
                        }
                    }
                }
            } else if (node.k === "bin") {
                visit(node.l, inAggregate);
                visit(node.r, inAggregate);
            } else if (node.k === "neg") {
                visit(node.e, inAggregate);
            } else if (node.k === "call") {
                const agg = !!FUNCTIONS[node.name].aggregate;
                node.args.forEach((a) => visit(a, agg));
            }
        };
        visit(ast, false);
        deps[key] = out;
    });

    const order = [];
    const state = {};
    let cycle = null;
    const walk = (key, stack) => {
        if (state[key] === 2) return;
        if (state[key] === 1) {
            cycle = stack.slice(stack.indexOf(key));
            return;
        }
        state[key] = 1;
        stack.push(key);
        deps[key].forEach((d) => walk(d, stack));
        stack.pop();
        state[key] = 2;
        order.push(key);
    };
    keys.forEach((k) => walk(k, []));
    return { keys: cycle ? order.filter((k) => cycle.indexOf(k) === -1) : order, cycle };
}

/*
  Checks a table configuration the way the server does when the question is
  saved. Returns a list of {where, code, message}; empty means valid.
*/
export function validateTableSettings(settings) {
    const problems = [];
    const columns = (settings && settings.columns) || [];
    const rows = (settings && settings.rows) || {};
    if (!columns.length) problems.push({ where: "columns", code: "empty", message: "Adicione pelo menos uma coluna" });
    if (columns.length > 30) problems.push({ where: "columns", code: "too_many", message: "No máximo 30 colunas" });
    const seen = {};
    columns.forEach((col, j) => {
        if (!col || !String(col.label || "").trim()) {
            problems.push({ where: columnLetter(j), code: "label", message: "Coluna " + columnLetter(j) + " sem título" });
        }
        if (col && col.alias) {
            const a = String(col.alias).toLowerCase();
            if (!/^[a-z_][a-z0-9_]*$/.test(a) || CELL_RE.test(a.toUpperCase()) || COLUMN_RE.test(a.toUpperCase()) || FUNCTIONS[a.toUpperCase()]) {
                problems.push({ where: columnLetter(j), code: "alias", message: "Apelido inválido: " + col.alias });
            } else if (seen[a]) {
                problems.push({ where: columnLetter(j), code: "alias", message: "Apelido repetido: " + col.alias });
            }
            seen[a] = true;
        }
    });

    const parsed = {};
    const check = (key, where, cfg, col) => {
        if (!hasFormula(cfg)) return;
        if (!col || col.type !== "number") {
            problems.push({ where, code: "formula_type", message: where + ": fórmulas só em colunas de número" });
        }
        try {
            const ast = parseFormula(cfg.formula);
            parsed[key] = ast;
            checkReferences(ast, settings, key, where, problems);
        } catch (e) {
            parsed[key] = { error: e.code };
            problems.push({ where, code: e.code || "syntax", message: where + ": " + e.message });
        }
    };
    const fixed = rows.mode === "fixed";
    const editorDataRows = fixed ? (rows.items || []).length : 1;
    for (let i = 0; i < editorDataRows; i++) {
        columns.forEach((col, j) => check("d:" + i + ":" + j, columnLetter(j) + (i + 1), cellConfig(settings, i, j), col));
    }
    (rows.summary || []).forEach((s, k) => {
        columns.forEach((col, j) => check("s:" + k + ":" + j, columnLetter(j) + (editorDataRows + k + 1), summaryCellConfig(settings, k, j), col));
    });
    const order = formulaOrder(settings, parsed);
    if (order.cycle) {
        problems.push({ where: order.cycle.join(","), code: "circular", message: "Referência circular" });
    }
    return problems;
}

function checkReferences(ast, settings, key, where, problems) {
    const columns = settings.columns || [];
    const rows = settings.rows || {};
    const fixed = rows.mode === "fixed";
    const editorDataRows = fixed ? (rows.items || []).length : 1;
    const totalRows = editorDataRows + (rows.summary || []).length;
    const aliases = aliasMap(columns);
    const inSummary = key.startsWith("s:");
    const visit = (node, inAggregate) => {
        if (!node) return;
        if (node.k === "cell" || node.k === "range") {
            const cols = node.k === "cell" ? [node.col] : [node.c1, node.c2];
            const rws = node.k === "cell" ? [node.row] : [node.r1, node.r2];
            cols.forEach((c) => {
                if (c >= columns.length) problems.push({ where, code: "ref", message: where + ": coluna " + columnLetter(c) + " não existe" });
            });
            rws.forEach((r) => {
                if (r === null) return;
                if (r < 1 || r > totalRows) problems.push({ where, code: "ref", message: where + ": linha " + r + " não existe" });
                else if (!fixed && r === 1 && inSummary) problems.push({ where, code: "ref", message: where + ": numa linha de resumo use a coluna inteira (ex.: SUM(B:B))" });
                else if (!fixed && r > 1 && !inSummary) problems.push({ where, code: "ref", message: where + ": linhas dinâmicas só citam a própria linha" });
            });
        } else if (node.k === "alias") {
            if (aliases[node.name] === undefined) problems.push({ where, code: "ref", message: where + ": " + node.name + " não existe" });
            else if (inSummary && !inAggregate) problems.push({ where, code: "ref", message: where + ": numa linha de resumo use " + node.name + " dentro de uma função" });
        } else if (node.k === "bin") {
            visit(node.l, inAggregate);
            visit(node.r, inAggregate);
        } else if (node.k === "neg") {
            visit(node.e, inAggregate);
        } else if (node.k === "call") {
            const agg = !!FUNCTIONS[node.name].aggregate;
            node.args.forEach((a) => visit(a, agg));
        }
    };
    visit(ast, false);
}

/*
  Rewrites the references of every formula when the editor inserts, removes or
  moves a column or row, like a spreadsheet does. A reference to something that
  was removed becomes #REF, which never parses, so the question cannot be saved
  until it is fixed.

  op: {kind: "insertColumn", at} | {kind: "removeColumn", at}
      | {kind: "moveColumn", from, to}
      | {kind: "insertRow", at} | {kind: "removeRow", at} | {kind: "moveRow", from, to}
  Row numbers in ops are editor row numbers starting at 1.
*/
export function shiftFormula(formula, op) {
    if (typeof formula !== "string" || !formula.trim()) return formula;
    let ast;
    try {
        ast = parseFormula(formula);
    } catch {
        return formula;
    }
    let broken = false;
    const mapCol = (c) => {
        if (op.kind === "insertColumn") return c >= op.at ? c + 1 : c;
        if (op.kind === "removeColumn") {
            if (c === op.at) {
                broken = true;
                return c;
            }
            return c > op.at ? c - 1 : c;
        }
        if (op.kind === "moveColumn") return moveIndex(c, op.from, op.to);
        return c;
    };
    const mapRow = (r) => {
        if (r === null) return r;
        if (op.kind === "insertRow") return r >= op.at ? r + 1 : r;
        if (op.kind === "removeRow") {
            if (r === op.at) {
                broken = true;
                return r;
            }
            return r > op.at ? r - 1 : r;
        }
        if (op.kind === "moveRow") return moveIndex(r - 1, op.from - 1, op.to - 1) + 1;
        return r;
    };
    const walk = (node) => {
        if (node.k === "cell") {
            node.col = mapCol(node.col);
            node.row = mapRow(node.row);
        } else if (node.k === "range") {
            node.c1 = mapCol(node.c1);
            node.c2 = mapCol(node.c2);
            node.r1 = mapRow(node.r1);
            node.r2 = mapRow(node.r2);
        } else if (node.k === "bin") {
            walk(node.l);
            walk(node.r);
        } else if (node.k === "neg") {
            walk(node.e);
        } else if (node.k === "call") {
            node.args.forEach(walk);
        }
    };
    walk(ast);
    if (broken) return "#REF";
    return formulaToString(ast);
}

function moveIndex(i, from, to) {
    if (i === from) return to;
    if (from < to && i > from && i <= to) return i - 1;
    if (from > to && i >= to && i < from) return i + 1;
    return i;
}

export default {
    parseFormula,
    evaluate,
    computeTable,
    validateTableSettings,
    shiftFormula,
    formulaToString,
    columnLetter,
    columnIndex,
    isRowFilled,
    cellConfig,
    summaryCellConfig,
    hasFormula,
    isLockedConfig,
    FormulaError,
    FUNCTION_NAMES,
};
